import { useTranslation } from 'react-i18next'
import { Icon } from '@/components/ui/icon'

interface FAQItem {
  readonly question: string
  readonly answer: string
}

/**
 * Objections that survive the argument above.
 *
 * Two independent column flows rather than one long stack. Each column is its
 * own list, so opening an answer pushes only that column and never leaves a
 * hole beside it, which is what a plain two-column grid of `<details>` does.
 *
 * Kept on `<details>` so the answers are in the DOM for crawlers and readable
 * with JavaScript disabled, which matters more here than on most pages: the
 * same content is published as a FAQPage entity in the homepage JSON-LD.
 */
export function LandingFAQ() {
  const { t } = useTranslation()
  const items = t('landing:faq.items', { returnObjects: true }) as FAQItem[]

  // Balanced split, longer column first, so an odd count leans left.
  const pivot = Math.ceil(items.length / 2)
  const columns = [items.slice(0, pivot), items.slice(pivot)]

  return (
    <section
      id="faq"
      aria-labelledby="faq-title"
      className="scroll-mt-24 border-t py-20 sm:py-28"
      style={{ borderColor: 'var(--lp-rule)' }}
    >
      <div className="mx-auto w-full max-w-6xl px-6 lg:px-10">
        <h2
          id="faq-title"
          className="text-balance text-3xl font-semibold tracking-[-0.025em] sm:text-4xl"
          style={{ color: 'var(--lp-ink)' }}
        >
          {t('landing:faq.title')}
        </h2>
        <p
          className="mt-4 max-w-[33rem] text-pretty text-lg leading-relaxed"
          style={{ color: 'var(--lp-body)' }}
        >
          {t('landing:faq.subtitle')}
        </p>

        <div className="mt-12 grid gap-x-16 sm:mt-14 md:grid-cols-2">
          {columns.map((column, columnIndex) => (
            <div
              key={columnIndex}
              className={
                columnIndex === 0 ? 'border-t' : 'border-t max-md:border-t-0'
              }
              style={{ borderColor: 'var(--lp-rule-strong)' }}
            >
              {column.map((item) => (
                <details
                  key={item.question}
                  className="group border-b"
                  style={{ borderColor: 'var(--lp-rule)' }}
                >
                  <summary
                    className="flex cursor-pointer list-none items-start justify-between gap-5 py-5 text-lg font-medium leading-snug tracking-[-0.01em] marker:content-none [&::-webkit-details-marker]:hidden"
                    style={{ color: 'var(--lp-ink)' }}
                  >
                    <span className="text-pretty">{item.question}</span>
                    <Icon
                      name="nav.chevronDown"
                      size="sm"
                      className="mt-1.5 shrink-0 transition-transform duration-200 group-open:rotate-180"
                      aria-hidden
                    />
                  </summary>
                  <p
                    className="max-w-[33rem] pb-6 pr-8 leading-[1.7]"
                    style={{ color: 'var(--lp-body)' }}
                  >
                    {item.answer}
                  </p>
                </details>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
